import type { Router } from 'vue-router';
import type { FilterState } from '../runtime/services/FilterService';

interface ViewStateStore
{
    key: string;
    getFilters(key: string): FilterState;
    setFilters(key: string, item: FilterState): void;
    getPager(key: string): PagerContract;
    setPager(key: string, item: PagerContract): void;
    getColumns(key: string): Columns;
    setColumns(key: string, item: Columns): void;
}

interface ViewState
{
    restore(key: string, filter?: FormType<any>, pager?: Pager, columns?: Columns): void;
    persist(key: string, filter?: FormType<any>, pager?: Pager, columns?: Columns): void;
    setFilterState(state: FilterState): void;
    getFilterState(): FilterState;
    setColumnsState(state: Columns): void;
    getColumnsState(): Columns;
}

class ViewStateHelper implements ViewState
{
    private router: Router;
    private store: ViewStateStore;

    public constructor(router: Router, store: ViewStateStore)
    {
        this.router = router;
        this.store = store;
    }

    private routeKey()
    {
        return this.router.currentRoute.value.name.toString();
    }

    public restore(key: string, filter?: FormType<any>, pager?: Pager, columns?: Columns)
    {
        this.store.key = key || this.routeKey();

        const filterState = this.store.getFilters(this.store.key);
        const pagerState = this.store.getPager(this.store.key);
        const columnsState = this.store.getColumns(this.store.key);

        if (filter != undefined)
        {
            filter.withData(filterState.form || JSON.parse(filterState.activeTemplate?.contentJson || '{}'));
        }

        if (pager != undefined && instanceOfPagerContract(pagerState))
        {
            pager.apply(pagerState);
        }

        if (columns != undefined)
        {
            if (!isObjectEmpty(columnsState.visible))
                columns.visible = columnsState.visible;

            if (!isObjectEmpty(columnsState.positions))
                columns.positions = columnsState.positions;
        }
    }

    public persist(key: string, filter?: FormType<any>, pager?: Pager, columns?: Columns)
    {
        this.store.key = key || this.routeKey();

        if (filter != undefined)
        {
            const filterState = this.store.getFilters(this.store.key);

            this.store.setFilters(this.store.key, {
                activeTemplate: filterState.activeTemplate,
                visible: filterState.visible,
                form: filter.data()
            });
        }

        if (pager != undefined)
        {
            this.store.setPager(this.store.key, pager.data());
        }

        if (columns != undefined)
        {
            this.store.setColumns(this.store.key, JSON.parse(JSON.stringify(columns)));
        }
    }

    public getFilterState()
    {
        return this.store.getFilters(this.store.key || this.routeKey());
    }

    public setFilterState(state: FilterState)
    {
        this.store.setFilters(this.store.key || this.routeKey(), state);
    }

    public getColumnsState()
    {
        return this.store.getColumns(this.store.key || this.routeKey());
    }

    public setColumnsState(state: Columns)
    {
        this.store.setColumns(this.store.key || this.routeKey(), JSON.parse(JSON.stringify(state)));
    }
}

function isObjectEmpty(object: any)
{
    for (const prop in object)
    {
        if (object.hasOwnProperty(prop))
        {
            return false;
        }
    }

    return true;
}

export default defineNuxtPlugin({
    name: 'viewstate',
    dependsOn: ['nuxt:router'],
    async setup(nuxtApp)
    {
        const { $router } = useNuxtApp();
        const store = useViewStateStore();
        const helper = new ViewStateHelper($router, store) as ViewState;

        return {
            provide: {
                viewstate: helper
            }
        };
    }
});

declare module '@vue/runtime-core'
{
    interface ComponentCustomProperties
    {
        $viewstate: ViewState;
    }
}
